import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { NextPageWithLayout } from '@/layouts/types'
import { ToastProvider } from '@/molecules/Toast/ToastProvider'
import { Page, PageSeo } from '@/page'
import { getPageByPath } from '@/services/CmsService/Pages'
import { PageDataContext, PageModel, fetchPageDataContext } from '@/services/RenderService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

interface CategoryPageProps {
  pageDataContext: PageDataContext
  page: PageModel
  locale: string
}

const CategoryPage: NextPageWithLayout<CategoryPageProps> = ({ page, pageDataContext, locale }) => {
  return (
    <>
      <PageSeo page={page} locale={locale} />
      <div className="bg-core-gray-950 pb-10 pt-28 text-white">
        <main className="flex flex-col gap-8">
          <ToastProvider>
            <Page page={page} pageDataContext={{ ...pageDataContext, contentItemConfig: { padding: true } }} />
          </ToastProvider>
        </main>
      </div>
    </>
  )
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<CategoryPageProps>(
  { filePath: 'pages/[locale]/category/[slug]/index.tsx' },
  async ({ locale, next404, nextRedirect, params, preview, federationClient }) => {
    const slug = getItemOrFirstEntry(params.slug)
    if (!slug) return next404()
    if (slug === 'kids-shows') return nextRedirect(paths.category.family, Durations.ONE_DAY_IN_SECONDS)

    const page = await getPageByPath(paths.category.index + '/' + slug, { locale, preview })
    if (!page) return next404(Durations.FIVE_MINUTES_IN_SECONDS)

    const [pageDataContext, translations] = await Promise.all([
      fetchPageDataContext(page, preview, locale, federationClient),
      loadTranslations(locale, ['common', 'account', 'category', 'watch']),
    ])
    if (!pageDataContext) return next404(Durations.FIVE_MINUTES_IN_SECONDS)

    return {
      props: {
        locale,
        page,
        pageDataContext,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default CategoryPage
